"use client"

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { usePathname } from "next/navigation"
import { Link } from "utils"
import type { components } from "umbraco"
import { getMediaUrl } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { replaceRichTextLinks } from "utils"
import Container from "../../Container"
import { useIsDesktop } from "../../theme"
import ShareLink from "../../icons/ShareLink"
import ShareFacebook from "../../icons/ShareFacebook"
import ShareX from "../../icons/ShareX"
import ShareLinkedIn from "../../icons/ShareLinkedIn"
import ShareWhatsApp from "../../icons/ShareWhatsApp"
import GradientChevron from "../../icons/GradientChevron"
import ArrowLeft from "../../icons/ArrowLeft"
import ArrowDown from "../../icons/ArrowDown"
import {
  Root,
  TwoColumnLayout,
  ContentColumn,
  SidebarColumn,
  MobileTocRoot,
  MobileTocBar,
  MobileTocBarText,
  MobileTocCaret,
  MobileTocPanel,
  MobileTocRow,
  MobileTocRowText,
  AnchorItem,
  AnchorText,
  AnchorChevron,
  TextContainer,
  CategoriesRow,
  CategoryChip,
  ArticleTitle,
  Subtitle,
  MetaBar,
  DateLocationGroup,
  DateSocialRow,
  DateText,
  MetaDivider,
  ReadingTimeText,
  LocationContainer,
  LocationLabel,
  LocationValue,
  SocialShareRow,
  ShareButton,
  CopyButtonWrapper,
  CopiedTooltip,
  HeroImageWrapper,
  HeroImageDesktop,
  HeroImageMobile,
  BodyContentWrapper,
  VideoWrapper,
  RelatedSection,
  RelatedTitle,
  RelatedGrid,
  RelatedCard,
  RelatedCardImageWrapper,
  RelatedCardContent,
  RelatedCardHeading,
  RelatedCardTitle,
  RelatedCardExcerpt,
  RelatedCardFooter,
  RelatedCtaLink,
  RelatedCtaText,
  RelatedCtaArrow,
  RelatedDateText,
  RelatedCategoryBadge,
  RelatedBadgeText,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type MagazineArticleData =
  components["schemas"]["MagazineArticlePropertiesModel"]

interface RelatedArticleItem {
  title: string
  excerpt: string
  category: string
  publishDate: string
  imageUrl: string | null
  slug: string
  routePath: string
}

interface Props {
  data: MagazineArticleData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  relatedArticles?: RelatedArticleItem[]
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getResponsiveImageUrl(blockList: unknown) {
  const list = blockList as
    | { items?: Array<{ content: { properties: Record<string, unknown> } }> }
    | null
    | undefined
  const item = list?.items?.[0]
  const props = item?.content?.properties
  if (!props) return { desktop: null, mobile: null, alt: "" }

  const desktopRel = getMediaUrl(props.desktopImage)
  const mobileRel = getMediaUrl(props.mobileImage)

  return {
    desktop: desktopRel ? getUmbracoMediaUrl(desktopRel) : null,
    mobile: mobileRel
      ? getUmbracoMediaUrl(mobileRel)
      : desktopRel
        ? getUmbracoMediaUrl(desktopRel)
        : null,
    alt: (props.altText as string) ?? "",
  }
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return ""
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    })
  } catch {
    return ""
  }
}

function getReadingTimeLabel(culture: string): string {
  switch (culture) {
    case "en-US":
    case "en":
      return "min read"
    case "ar":
      return "دقائق قراءة"
    case "ru":
      return "мин чтения"
    default:
      return "דק׳ קריאה"
  }
}

function getLocationLabel(culture: string): string {
  switch (culture) {
    case "en-US":
    case "en":
      return "Location:"
    case "ar":
      return "الموقع:"
    case "ru":
      return "Место:"
    default:
      return "מיקום:"
  }
}

function getRelatedArticlesTitle(culture: string): string {
  switch (culture) {
    case "en-US":
    case "en":
      return "Related Articles"
    case "ar":
      return "مقالات ذات صلة"
    case "ru":
      return "Похожие статьи"
    default:
      return "כתבות נוספות"
  }
}

function getCtaLabel(culture: string): string {
  switch (culture) {
    case "en-US":
    case "en":
      return "Read more"
    case "ar":
      return "اقرأ المزيد"
    case "ru":
      return "Читать далее"
    default:
      return "לקרוא עוד"
  }
}

function extractYouTubeId(url: string | null | undefined): string | null {
  if (!url) return null
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]+)/,
  )
  return match?.[1] ?? null
}

/**
 * Resolve the article "categories" value to label strings. EGDE-375: categories
 * are now managed content nodes picked via a MultiNodeTreePicker, so the Delivery
 * API serialises the value as an array of category items ({ name, … }) instead
 * of plain strings. Read each node's `name`; the string branch keeps any
 * not-yet-migrated value rendering instead of crashing the article page.
 */
function getCategoryNames(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((entry) => {
      if (typeof entry === "string") return entry
      if (entry && typeof entry === "object") {
        return ((entry as Record<string, unknown>).name as string) ?? ""
      }
      return ""
    })
    .filter((name): name is string => Boolean(name))
}

/**
 * Parse H1–H3 headings from rich text markup for the anchor nav links.
 * Returns array of { id, text, level } objects.
 *
 * EGDE-392: match H1 too — editors author the section title at the shallowest
 * heading level they use (often H1, e.g. "אחד") and sub-sections one level
 * deeper. The previous regex only matched H2/H3, so an H1 title was dropped from
 * the anchor list entirely (the "missing title" the QA screenshots flagged).
 */
function extractHeadings(
  markup: string | null | undefined,
): Array<{ id: string; text: string; level: 1 | 2 | 3 }> {
  if (!markup) return []
  const regex = /<h([123])[^>]*>([\s\S]*?)<\/h[123]>/gi
  const headings: Array<{ id: string; text: string; level: 1 | 2 | 3 }> = []
  let match = regex.exec(markup)
  while (match) {
    const level = (Number(match[1]) as 1 | 2 | 3) ?? 2
    // Strip inner tags, then decode the HTML entities the RTE commonly emits
    // (e.g. a trailing &nbsp;) so labels read as plain text, not "אחד&nbsp;".
    const text = match[2]
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#3(?:9|4);/g, (m) => (m === "&#39;" ? "'" : '"'))
      .replace(/\s+/g, " ")
      .trim()
    if (text) {
      const id = `heading-${headings.length}`
      headings.push({ id, text, level })
    }
    match = regex.exec(markup)
  }
  return headings
}

/**
 * Inject id attributes into H1–H3 tags so the anchor links resolve. The index
 * order MUST match extractHeadings so `heading-N` targets the right heading.
 */
function injectHeadingIds(markup: string): string {
  let index = 0
  return markup.replace(/<(h[123])([^>]*)>/gi, (fullMatch, tag, attrs) => {
    const id = `heading-${index}`
    index++
    // If there's already an id, skip
    if (/id\s*=/.test(attrs)) return fullMatch
    return `<${tag}${attrs} id="${id}">`
  })
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Social share buttons.
 * RTL source order: [WhatsApp, LinkedIn, X, Facebook, Link]
 *   => visual L->R in RTL: [Link, Facebook, X, LinkedIn, WhatsApp]
 *   which matches the Figma design.
 */
function SocialShareButtons({ title }: { title: string | null | undefined }) {
  const [pageUrl, setPageUrl] = useState("")
  const [copied, setCopied] = useState(false)
  const t = useTranslations("InnerPages")

  useEffect(() => {
    setPageUrl(encodeURIComponent(window.location.href))
  }, [])

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [])

  const pageTitle = encodeURIComponent(title ?? "")

  return (
    <SocialShareRow>
      <ShareButton
        href={`https://api.whatsapp.com/send?text=${pageTitle}%20${pageUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on WhatsApp"
      >
        <ShareWhatsApp />
      </ShareButton>
      <ShareButton
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${pageUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
      >
        <ShareLinkedIn />
      </ShareButton>
      <ShareButton
        href={`https://twitter.com/intent/tweet?url=${pageUrl}&text=${pageTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X"
      >
        <ShareX />
      </ShareButton>
      <ShareButton
        href={`https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Facebook"
      >
        <ShareFacebook />
      </ShareButton>
      <CopyButtonWrapper>
        <ShareButton
          as="button"
          onClick={handleCopyLink}
          aria-label={copied ? t("linkCopied") : "Copy link"}
          style={{
            border: "none",
            background: "none",
            padding: 0,
            opacity: copied ? 0.5 : 1,
          }}
        >
          <ShareLink />
        </ShareButton>
        {copied && <CopiedTooltip>{t("linkCopied")}</CopiedTooltip>}
      </CopyButtonWrapper>
    </SocialShareRow>
  )
}

// Distance below the viewport top at which a heading counts as the active
// section for the scroll-spy — clears the fixed header pill + sticky TOC bar.
const SPY_OFFSET = 140

/** Shared scroll-spy: returns the id of the section currently in view. */
function useActiveHeading(
  headings: Array<{ id: string; text: string; level: 1 | 2 | 3 }>,
) {
  const [activeId, setActiveId] = useState<string>(headings[0]?.id ?? "")
  const isScrollingRef = useRef(false)

  useEffect(() => {
    if (headings.length === 0) return

    const handleScroll = () => {
      if (isScrollingRef.current) return
      let current = headings[0]?.id ?? ""
      for (const heading of headings) {
        const el = document.getElementById(heading.id)
        if (el && el.getBoundingClientRect().top <= SPY_OFFSET) {
          current = heading.id
        }
      }
      setActiveId(current)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [headings])

  const scrollTo = useCallback((id: string) => {
    setActiveId(id)
    isScrollingRef.current = true
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
      setTimeout(() => {
        isScrollingRef.current = false
      }, 1000)
    }
  }, [])

  return { activeId, scrollTo }
}

/**
 * Desktop sidebar anchor rail (hidden below lg). Flat list of every section,
 * active one highlighted, with scroll-spy + smooth-scroll.
 */
function SidebarNav({
  headings,
}: {
  headings: Array<{ id: string; text: string; level: 1 | 2 | 3 }>
}) {
  const { activeId, scrollTo } = useActiveHeading(headings)

  const handleClick = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault()
      scrollTo(id)
    },
    [scrollTo],
  )

  if (headings.length === 0) return null

  return (
    <SidebarColumn>
      {headings.map((heading) => {
        const isActive = heading.id === activeId
        return (
          <AnchorItem
            key={heading.id}
            href={`#${heading.id}`}
            $active={isActive}
            onClick={(e) => handleClick(e, heading.id)}
          >
            <AnchorChevron>
              <GradientChevron width={12} height={20} />
            </AnchorChevron>
            <AnchorText variant="body1" $active={isActive}>
              {heading.text}
            </AnchorText>
          </AnchorItem>
        )
      })}
    </SidebarColumn>
  )
}

/**
 * EGDE-392 / EGDE-393: mobile anchor nav — a collapsible anchor dropdown pinned
 * to the BOTTOM of the viewport while the article is in view (a top-sticky bar
 * collided with the collapsing header while scrolling). The collapsed bar shows
 * the active section (incl. the H1 title that used to go missing); tapping it
 * opens the full anchor list upward. Slides away when the article scrolls off.
 */
function MobileTableOfContents({
  headings,
  watchRef,
}: {
  headings: Array<{ id: string; text: string; level: 1 | 2 | 3 }>
  watchRef: React.RefObject<HTMLElement | null>
}) {
  const { activeId, scrollTo } = useActiveHeading(headings)
  const [open, setOpen] = useState(false)
  // EGDE-393: only show the bottom bar while the article body is on screen so it
  // doesn't float over the header, footer, or neighbouring page blocks.
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = watchRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { rootMargin: "-80px 0px 0px 0px" },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [watchRef])

  const handleSelect = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault()
      setOpen(false)
      scrollTo(id)
    },
    [scrollTo],
  )

  if (headings.length === 0) return null

  const activeHeading = headings.find((h) => h.id === activeId) ?? headings[0]

  return (
    <MobileTocRoot $visible={visible}>
      <MobileTocBar
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <AnchorChevron>
          <GradientChevron width={12} height={20} />
        </AnchorChevron>
        <MobileTocBarText variant="body1">
          {activeHeading?.text}
        </MobileTocBarText>
        <MobileTocCaret $open={open}>
          <ArrowDown width={16} height={8} />
        </MobileTocCaret>
      </MobileTocBar>

      {open && (
        <MobileTocPanel>
          {headings.map((heading) => {
            const isActive = heading.id === activeId
            return (
              <MobileTocRow
                key={heading.id}
                href={`#${heading.id}`}
                $active={isActive}
                onClick={(e) => handleSelect(e, heading.id)}
              >
                <AnchorChevron>
                  <GradientChevron width={12} height={20} />
                </AnchorChevron>
                <MobileTocRowText variant="body1" $active={isActive}>
                  {heading.text}
                </MobileTocRowText>
              </MobileTocRow>
            )
          })}
        </MobileTocPanel>
      )}
    </MobileTocRoot>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const MAX_RELATED_ARTICLES = 3

export default function MagazineArticle({
  data,
  culture,
  relatedArticles,
}: Props) {
  const isDesktop = useIsDesktop()
  const pathname = usePathname()
  // EGDE-393: the bottom anchor bar shows only while this element is on screen.
  const contentRef = useRef<HTMLDivElement>(null)

  // Hero image
  const heroImage = getResponsiveImageUrl(data.heroImage)

  // Body content headings for sidebar
  const bodyMarkup = data.bodyContent?.markup ?? ""
  const headings = useMemo(() => extractHeadings(bodyMarkup), [bodyMarkup])
  const processedMarkup = useMemo(
    () => (bodyMarkup ? replaceRichTextLinks(injectHeadingIds(bodyMarkup)) : ""),
    [bodyMarkup],
  )

  // Date string with prefix + locale-aware reading time
  const rawDate = formatDate(data.publishDate)
  const datePrefix = culture.startsWith("en") ? "Updated on " : "עודכן ב-"
  const dateStr = rawDate ? `${datePrefix}${rawDate}` : ""
  const readingTimeLabel = getReadingTimeLabel(culture)
  const readingTimeStr = data.readingTime
    ? `${data.readingTime} ${readingTimeLabel}`
    : ""

  // YouTube video
  const youtubeId = extractYouTubeId(data.videoUrl)

  // Categories
  const categories = getCategoryNames(data.categories)

  // Locale-aware labels
  const locationLabel = getLocationLabel(culture)
  const relatedTitle = getRelatedArticlesTitle(culture)
  const ctaLabel = getCtaLabel(culture)

  // Related articles — exclude current article by matching slug from pathname
  const currentSlug = pathname.split("/").filter(Boolean).pop() ?? ""
  const filteredRelated = useMemo(() => {
    if (!relatedArticles) return []
    return relatedArticles
      .filter((a) => a.slug !== currentSlug)
      .slice(0, MAX_RELATED_ARTICLES)
  }, [relatedArticles, currentSlug])

  return (
    <Container paddingMobile>
      <Root>
        {/* EGDE-392/393: mobile anchor nav — dropdown pinned to the bottom of
            the viewport while the article body is on screen. Desktop uses the
            sidebar rail instead. */}
        {!isDesktop && (
          <MobileTableOfContents headings={headings} watchRef={contentRef} />
        )}

        <TwoColumnLayout>
          {/* RTL: source [Sidebar, Content]
              => visual: [Content RIGHT, Sidebar LEFT]
              Figma shows Content on RIGHT (inline-start), Sidebar on LEFT */}
          {isDesktop && <SidebarNav headings={headings} />}

          <ContentColumn ref={contentRef}>
            {/* Text container: categories, title, subtitle, meta bar */}
            <TextContainer>
              {/* Title */}
              <ArticleTitle variant="h2">{data.title}</ArticleTitle>

              {/* Subtitle */}
              {data.subtitle && (
                <Subtitle variant="body1">{data.subtitle}</Subtitle>
              )}

              {/* Meta bar — location on its own row, then date + social on one row */}
              <MetaBar>
                {data.location && (
                  <LocationContainer>
                    <LocationLabel>{locationLabel}</LocationLabel>
                    <LocationValue>{data.location}</LocationValue>
                  </LocationContainer>
                )}
                <DateSocialRow>
                  <DateLocationGroup>
                    {dateStr && <DateText>{dateStr}</DateText>}
                    {/* EGDE-103: pipe divider between the updated-date and the
                        reading time, on every viewport (was missing on mobile). */}
                    {dateStr && readingTimeStr && <MetaDivider />}
                    {readingTimeStr && (
                      <ReadingTimeText>{readingTimeStr}</ReadingTimeText>
                    )}
                  </DateLocationGroup>
                  <SocialShareButtons title={data.title} />
                </DateSocialRow>
              </MetaBar>
            </TextContainer>

            {/* Categories chips */}
            {categories.length > 0 && (
              <CategoriesRow>
                {categories.map((cat, idx) => (
                  <CategoryChip key={idx}>{cat}</CategoryChip>
                ))}
              </CategoriesRow>
            )}

            {/* Hero image — EGDE-332: mobile gets its own crop (falls back to
                desktop), toggled by breakpoint instead of always desktop. */}
            {heroImage.desktop && (
              <HeroImageWrapper>
                <HeroImageDesktop>
                  <Image
                    src={heroImage.desktop}
                    alt={heroImage.alt || data.title || ""}
                    fill
                    sizes="800px"
                    style={{ objectFit: "cover" }}
                    priority
                  />
                </HeroImageDesktop>
                <HeroImageMobile>
                  <Image
                    src={heroImage.mobile ?? heroImage.desktop}
                    alt={heroImage.alt || data.title || ""}
                    fill
                    sizes="100vw"
                    style={{ objectFit: "cover" }}
                    priority
                  />
                </HeroImageMobile>
              </HeroImageWrapper>
            )}

            {/* Body content (Rich Text) */}
            {processedMarkup && (
              <BodyContentWrapper
                dangerouslySetInnerHTML={{ __html: processedMarkup }}
              />
            )}

            {/* YouTube video embed. EGDE-336: match the dedicated EmbeddedVideo
                block's embed exactly — privacy-enhanced youtube-nocookie domain
                + rel=0 — so the article video renders with the same YouTube
                chrome instead of a differently-styled default embed. */}
            {youtubeId && (
              <VideoWrapper>
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0`}
                  title="YouTube video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </VideoWrapper>
            )}
          </ContentColumn>
        </TwoColumnLayout>

        {/* Related articles section */}
        {filteredRelated.length > 0 && (
          <RelatedSection>
            <RelatedTitle variant="h3">{relatedTitle}</RelatedTitle>
            <RelatedGrid>
              {filteredRelated.map((article) => (
                <RelatedCard
                  key={article.slug}
                  as={Link}
                  href={article.routePath}
                >
                  {article.imageUrl && (
                    <RelatedCardImageWrapper>
                      <Image
                        src={article.imageUrl}
                        alt={article.title}
                        fill
                        sizes="(max-width: 1279px) 100vw, 384px"
                        style={{ objectFit: "cover" }}
                      />
                      {article.category && (
                        <RelatedCategoryBadge>
                          <RelatedBadgeText variant="body1">
                            {article.category}
                          </RelatedBadgeText>
                        </RelatedCategoryBadge>
                      )}
                    </RelatedCardImageWrapper>
                  )}
                  <RelatedCardContent>
                    <RelatedCardHeading>
                      <RelatedCardTitle variant="body1">
                        {article.title}
                      </RelatedCardTitle>
                      {article.excerpt && (
                        <RelatedCardExcerpt variant="body2">
                          {article.excerpt}
                        </RelatedCardExcerpt>
                      )}
                    </RelatedCardHeading>
                    <RelatedCardFooter>
                      <RelatedCtaLink>
                        <RelatedCtaText variant="body1">
                          {ctaLabel}
                        </RelatedCtaText>
                        <RelatedCtaArrow>
                          <ArrowLeft width={11} height={8} />
                        </RelatedCtaArrow>
                      </RelatedCtaLink>
                      {article.publishDate && (
                        <RelatedDateText variant="body2">
                          {formatDate(article.publishDate)}
                        </RelatedDateText>
                      )}
                    </RelatedCardFooter>
                  </RelatedCardContent>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedSection>
        )}
      </Root>
    </Container>
  )
}
